import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { PALETA, TvChart } from "../trading/TvChart";
import { TradingPanel } from "../trading/Panel";
import { http, errMsg } from "../../lib/api";
import { saveBlob } from "../../lib/useOp";
import { fmt, pct } from "../../lib/lm";
import { Btn, Card } from "../kit/Kit";
import { fDia } from "../../lib/trading";

const MET = [["programado", "Capital programado acum."], ["cobrado", "Capital cobrado acum."], ["dpd30", "Saldo 30+ DPD"], ["dpd60", "Saldo 60+ DPD"], ["dpd90", "Saldo 90+ DPD"]];

const Seg = ({ opts, v, set, testid }) => (
  <div className="flex rounded-xl border border-[#E2DDD5] p-0.5" data-testid={testid}>
    {opts.map(([k, l]) => <button key={k} onClick={() => set(k)} data-testid={`${testid}-${k}`} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${v === k ? "bg-[#111] text-white" : "text-[#5C5850]"}`}>{l}</button>)}
  </div>
);

export const Vintage = ({ ds }) => {
  const [periodo, setPeriodo] = useState("mes");
  const [met, setMet] = useState("cobrado");
  const [base, setBase] = useState("pct");
  const [vista, setVista] = useState("tabla");
  const [v, setV] = useState(null);
  useEffect(() => { http.get("/lm/explorer/vintage", { params: { dataset: ds, periodo } }).then((r) => setV(r.data)).catch((e) => toast.error(errMsg(e))); }, [ds, periodo]);
  const key = base === "pct" ? `${met}_pct` : met;
  const show = (x) => (x === undefined ? "" : base === "pct" ? pct(x, 1) : fmt(x));
  const xlsx = async () => { try { const r = await http.get("/lm/explorer/vintage/xlsx", { params: { dataset: ds, periodo }, responseType: "blob" }); saveBlob(r.data, `vintage_${periodo}.xlsx`); } catch (e) { toast.error(errMsg(e)); } };
  if (!v) return <p className="text-sm text-[#8C867A]">Calculando…</p>;
  const mobs = Array.from({ length: v.max_mob + 1 }, (_, i) => i);
  const curva = mobs.map((i) => Object.fromEntries([["mob", i], ...v.cohortes.map((c) => [c.cohorte, c.mob[i]?.[key]])]));
  return (
    <Card title="Vintage raw data" subtitle={`Cohortes por ${periodo} de disposición · MOB 0 = mes de disposición · corte ${fDia(v.corte, true)}`} testid="vintage"
      right={<Btn kind="secondary" onClick={xlsx} data-testid="vintage-xlsx"><Download size={14} />Excel</Btn>}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Seg opts={[["mes", "Mensual"], ["trimestre", "Trimestral"]]} v={periodo} set={setPeriodo} testid="vintage-periodo" />
        <Seg opts={MET.map(([k, l]) => [k, l])} v={met} set={setMet} testid="vintage-metrica" />
        <Seg opts={[["monto", "$"], ["pct", "% del original"]]} v={base} set={setBase} testid="vintage-base" />
        <Seg opts={[["tabla", "Triángulo"], ["curva", "Curvas"]]} v={vista} set={setVista} testid="vintage-vista" />
      </div>
      {vista === "tabla" ? (
        <div className="overflow-auto" data-testid="vintage-tabla">
          <table className="w-full text-xs">
            <thead className="bg-[#F7F5F1]"><tr><th className="px-2 py-2 text-left">Cohorte</th><th className="px-2 py-2">#</th><th className="px-2 py-2 text-right">Original</th>{mobs.map((i) => <th key={i} className="px-2 py-2 text-right">MOB {i}</th>)}</tr></thead>
            <tbody>{v.cohortes.map((c) => (
              <tr key={c.cohorte} className="border-t border-[#EFEBE4]" data-testid={`vintage-row-${c.cohorte}`}>
                <td className="px-2 py-1.5 font-semibold">{c.cohorte}</td><td className="px-2 py-1.5 text-center">{c.creditos}</td><td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.monto_original)}</td>
                {mobs.map((i) => <td key={i} className="px-2 py-1.5 text-right tabular-nums">{show(c.mob[i]?.[key])}</td>)}
              </tr>))}</tbody>
          </table>
        </div>
      ) : (
        <div data-testid="vintage-curvas">
          <TradingPanel title={`Curvas vintage · ${MET.find((m) => m[0] === met)[1]}`} testid="vintage-tp" fuente="Loan Management · /lm/explorer/vintage"
            como="Cada línea es una cohorte (mes o trimestre de disposición); eje X = meses en libros (MOB). Valores acumulados en $ o como % del monto original. Toca la leyenda para mostrar u ocultar cohortes.">
            <TvChart toggle height={320} labels={mobs.map((i) => `MOB ${i}`)} fmt={(x) => show(x)} testid="vintage-chart"
              series={v.cohortes.map((c, i) => ({ id: c.cohorte, name: c.cohorte, type: "line", color: PALETA[i % PALETA.length], data: curva.map((r) => (r[c.cohorte] == null ? {} : { value: r[c.cohorte] })) }))} />
          </TradingPanel>
        </div>
      )}
    </Card>
  );
};
